import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { ALL_TABS } from "../data/sidebarConfig";
import { Sparkles, X, Send, Loader2, MessageCircle } from "lucide-react";

export default function FloatingAIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Salut. Je suis ton coach stratégique IA, contextuel à la page où tu te trouves. Demande-moi : analyse de hook, idée vidéo, conseil algorithme, etc." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation();
  const scrollRef = useRef(null);

  // detect page context
  const exact = ALL_TABS.find(t => t.path === pathname);
  const prefix = !exact ? ALL_TABS.find(t => t.path !== "/" && pathname.startsWith(t.path)) : null;
  const pageLabel = (exact || prefix)?.label || "Dashboard";
  const platformMatch = pathname.match(/(instagram|tiktok|youtube|threads|x-twitter|snap|clapper)/i);
  const platformContext = platformMatch ? platformMatch[1] : "";

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const r = await api.aiChat({
        message: userMsg.content,
        page_context: pageLabel,
        platform_context: platformContext,
        history: newMessages.slice(-10),
      });
      setMessages(m => [...m, { role: "assistant", content: r.response || "..." }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Erreur — réessaie dans un instant." }]);
    } finally { setLoading(false); }
  };

  const quickActions = [
    "Analyse cette page",
    "Donne-moi 5 hooks",
    "Pourquoi mon reel flop ?",
    "Quel watchtime viser ?",
  ];

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="ai-assistant-btn"
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#CCFF00] hover:bg-[#B3E600] shadow-[0_0_30px_rgba(204,255,0,0.4)] flex items-center justify-center transition-all hover:scale-105 ${open ? "scale-0 opacity-0 pointer-events-none" : ""}`}
      >
        <Sparkles className="w-6 h-6 text-black" strokeWidth={2} />
      </button>

      {/* Drawer */}
      <div className={`fixed top-0 right-0 z-50 h-screen w-full sm:w-[420px] bg-[#0A0A0C] border-l border-[#1E1E24] flex flex-col transition-transform ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="ai-assistant-drawer">
        <header className="px-5 py-4 border-b border-[#1E1E24] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#CCFF00]/15 border border-[#CCFF00]/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-white">Coach IA</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Contexte: <span className="text-[#CCFF00]">{pageLabel}</span></div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" data-testid="ai-assistant-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2.5 rounded-sm text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#CCFF00] text-black"
                  : "bg-[#0C0C0E] border border-[#1E1E24] text-zinc-200"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#0C0C0E] border border-[#1E1E24] px-3 py-2.5 rounded-sm text-sm text-zinc-400 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin text-[#CCFF00]" /> Claude réfléchit...
              </div>
            </div>
          )}
        </div>

        {messages.length <= 2 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {quickActions.map(q => (
              <button key={q} onClick={() => setInput(q)} data-testid={`quick-${q.slice(0, 10)}`}
                className="text-[11px] font-mono px-2.5 py-1.5 rounded-sm border border-[#1E1E24] text-zinc-400 hover:text-white hover:border-[#CCFF00]/40 transition">
                {q}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 py-4 border-t border-[#1E1E24] flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Pose ta question..."
            data-testid="ai-assistant-input"
            className="flex-1 bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none"
          />
          <button onClick={send} disabled={loading || !input.trim()} data-testid="ai-assistant-send"
            className="bg-[#CCFF00] text-black px-3 rounded-sm hover:bg-[#B3E600] disabled:opacity-40 transition">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
}
